import {
  AppWindow,
  ArrowLeft,
  Building2,
  Gauge,
  Coffee,
  Lightbulb,
  OctagonAlert,
  Paperclip,
  Timer,
  User,
  UserRound,
  Users,
  Wrench,
  X,
  type LucideIcon,
} from "lucide-react";
import { useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { fieldErrors } from "../api/client";
import type { DirectoryMatch, Impact, TicketType, Urgency } from "../api/types";
import { can, useMe } from "../auth/session";
import { usePageTitle } from "../lib/hooks";
import { SERVICE_STATES } from "../lib/updates";
import { Button } from "../ui/Button";
import { TextAreaField, TextField, ToggleRow } from "../ui/Field";
import { Notice, errorMessage } from "../ui/Notice";
import { PageHeader } from "../ui/PageHeader";
import { Select } from "../ui/Select";
import { Spinner } from "../ui/Spinner";
import { useCatalogue, useCreateTicket, useTickets, type PersonChoice } from "./api";
import { DirectoryPicker } from "./DirectoryPicker";
import { PeopleField } from "./PeopleField";
import { ACCEPT, MAX_FILES, fileProblem } from "./files";
import {
  ENVIRONMENT_LABELS,
  IMPACT_DETAILS,
  IMPACT_LABELS,
  OPEN_STATUSES,
  PRIORITY_LABELS,
  URGENCY_DETAILS,
  URGENCY_DETAILS_REQUEST,
  URGENCY_LABELS,
  formatBytes,
  suggestedPriority,
} from "./labels";
import { SuggestedFixes } from "./SuggestedFixes";
import { StatusBadge } from "./TicketBadges";
import "./tickets.css";

const IMPACTS: Impact[] = ["single", "multiple", "widespread"];
const URGENCIES: Urgency[] = ["high", "medium", "low"];
const IMPACT_ICONS: Record<Impact, LucideIcon> = { single: User, multiple: Users, widespread: Building2 };
const URGENCY_ICONS: Record<Urgency, LucideIcon> = { high: OctagonAlert, medium: Timer, low: Coffee };
const TYPES: [TicketType, string, string, LucideIcon][] = [
  ["incident", "Incident", "Something isn't working as it should", Wrench],
  ["service_request", "Service request", "Access, equipment, software or a change", Lightbulb],
];
const FOR_WHOM: [boolean, string, string, LucideIcon][] = [
  [false, "Myself", "Raised in your name", User],
  [true, "A colleague", "Raised on their behalf", UserRound],
];
const CONTEXT_KEYS = ["app", "version", "screen", "correlation_id", "environment"] as const;

function choice(person: DirectoryMatch): PersonChoice {
  return { user_id: person.user_id, object_id: person.object_id, email: person.email, name: person.name };
}

/**
 * Raise a ticket. Support staff can also raise one for someone else (DECISIONS #44). Also the
 * target of app Support links (/tickets/new?service=navigator&...).
 */
export function NewTicketPage() {
  usePageTitle("Raise a ticket");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Context sent by an app's Support link. The server keeps only safe, known fields.
  const [appContext] = useState(() => {
    const context: Record<string, string> = {};
    for (const key of CONTEXT_KEYS) {
      const value = params.get(key);
      if (value) context[key] = value.slice(0, 500);
    }
    return Object.keys(context).length ? context : null;
  });
  const catalogue = useCatalogue();
  const create = useCreateTicket();
  // One id per form: a double click or retry returns the ticket already created.
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [type, setType] = useState<TicketType>("incident");
  const request = type === "service_request";
  const me = useMe();
  const canRaiseForOthers = me.permissions.includes("raise_on_behalf");
  const [forSomeoneElse, setForSomeoneElse] = useState(false);
  const [person, setPerson] = useState<DirectoryMatch | null>(null);
  const other = canRaiseForOthers && forSomeoneElse;
  // The person's pick; until they pick, a Support link's service (?service=navigator).
  const [serviceChoice, setServiceChoice] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [title, setTitle] = useState(() => (params.get("summary") ?? "").slice(0, 200));
  const [description, setDescription] = useState("");
  const [impact, setImpact] = useState<Impact>("single");
  const [urgency, setUrgency] = useState<Urgency>("medium");
  const [confidential, setConfidential] = useState(false);
  // Colleagues affected too, who share the ticket (DECISIONS #49).
  const [team, setTeam] = useState<DirectoryMatch[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const linkedSlug = params.get("service");
  const linkedService = catalogue.data?.find((s) => linkedSlug && s.slug === linkedSlug);
  const serviceId = serviceChoice ?? linkedService?.id ?? "";
  const service = useMemo(() => catalogue.data?.find((s) => s.id === serviceId), [catalogue.data, serviceId]);
  const category = service?.categories.find((c) => c.id === categoryId);
  const forcedConfidential = Boolean(service?.is_restricted);
  const isConfidential = confidential || forcedConfidential;
  const sharing = impact === "multiple" && !isConfidential;

  const problems = {
    title: title.trim().length < 5 ? "Give it a short title of at least 5 characters." : undefined,
    description: description.trim().length < 10 ? "Describe what happened, in at least 10 characters." : undefined,
    person: other && !person ? "Choose who the ticket is for." : undefined,
  };
  const server = fieldErrors(create.error);
  const fieldError = (field: keyof typeof problems) =>
    (showErrors ? problems[field] : undefined) ?? server.find((e) => e.field === field)?.message;

  function addFiles(list: FileList | null) {
    if (!list) return;
    const next = [...files, ...Array.from(list)];
    const bad = Array.from(list).map(fileProblem).find(Boolean);
    if (bad) setFileError(bad);
    else if (next.length > MAX_FILES) setFileError(`Attach at most ${MAX_FILES} files.`);
    else {
      setFileError(null);
      setFiles(next);
    }
    if (fileInput.current) fileInput.current.value = "";
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    setShowErrors(true);
    if (Object.values(problems).some(Boolean)) return;
    create.mutate(
      {
        input: {
          type,
          title: title.trim(),
          description: description.trim(),
          service_id: serviceId || null,
          category_id: subcategoryId || categoryId || null,
          impact,
          urgency,
          is_restricted: isConfidential,
          client_request_id: clientRequestId,
          app_context: appContext ?? undefined,
          on_behalf_of: other && person ? choice(person) : undefined,
          shared_with: sharing && team.length ? team.map(choice) : undefined,
        },
        files,
      },
      {
        onSuccess: ({ ticket, failedUpload }) => {
          const params = new URLSearchParams({ created: "1" });
          if (failedUpload) params.set("upload_failed", failedUpload);
          navigate(`/tickets/${ticket.reference}?${params.toString()}`);
        },
      },
    );
  }

  // Back to where staff work (Tickets), or to Help for everyone else.
  const back = can(me, "use_workspace") ? { to: "/tickets", label: "Tickets" } : { to: "/", label: "Help" };

  return (
    <>
      <Link to={back.to} className="back-link">
        <ArrowLeft size={16} aria-hidden="true" />
        {back.label}
      </Link>
      <PageHeader
        title="Raise a ticket"
        sub="Describe the issue. You'll get a reference instantly."
      />
      <div className="new-ticket-layout">
        <form className="card new-ticket" onSubmit={submit} noValidate>
          {appContext ? (
            <div className="app-context" role="note">
              <AppWindow size={18} aria-hidden="true" />
              <div>
                <span className="cell-strong">
                  Sent from {linkedService?.name ?? appContext.app ?? "an Aspect app"}
                  {appContext.version ? ` ${appContext.version}` : ""}
                  {appContext.environment
                    ? ` (${ENVIRONMENT_LABELS[appContext.environment] ?? appContext.environment})`
                    : ""}
                </span>
                <span className="cell-secondary">
                  {appContext.screen
                    ? `Screen: ${appContext.screen.split("?")[0]}`
                    : "Details from the app are attached to this ticket."}
                </span>
              </div>
            </div>
          ) : null}
          {canRaiseForOthers ? (
            <fieldset className="form-section">
              <legend className="form-legend">Requester</legend>
              <div className="option-grid option-grid-2">
                {FOR_WHOM.map(([value, label, detail, Icon]) => (
                  <label key={label} className="option-tile">
                    <input
                      type="radio"
                      name="for"
                      checked={forSomeoneElse === value}
                      onChange={() => setForSomeoneElse(value)}
                    />
                    <span className="option-icon" aria-hidden="true">
                      <Icon size={20} />
                    </span>
                    <span className="option-text">
                      <span className="option-label">{label}</span>
                      <span className="option-detail">{detail}</span>
                    </span>
                  </label>
                ))}
              </div>
              {forSomeoneElse ? (
                <>
                  <DirectoryPicker label="Colleague" value={person} onChange={setPerson} />
                  {showErrors && problems.person ? <span className="field-error">{problems.person}</span> : null}
                </>
              ) : null}
            </fieldset>
          ) : null}
          <fieldset className="form-section">
            <legend className="form-legend">Request type</legend>
            <div className="option-grid option-grid-2">
              {TYPES.map(([value, label, detail, Icon]) => (
                <label key={value} className="option-tile">
                  <input type="radio" name="type" checked={type === value} onChange={() => setType(value)} />
                  <span className="option-icon" aria-hidden="true">
                    <Icon size={20} />
                  </span>
                  <span className="option-text">
                    <span className="option-label">{label}</span>
                    <span className="option-detail">{detail}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="form-grid">
            <div className="field">
              <label className="field-label" htmlFor="service">
                Service
              </label>
              {catalogue.isPending ? (
                <Spinner label="Loading services" />
              ) : (
                <Select
                  id="service"
                  label="Service"
                  value={serviceId}
                  onChange={(value) => {
                    setServiceChoice(value);
                    setCategoryId("");
                    setSubcategoryId("");
                  }}
                  groups={[
                    { options: [{ value: "", label: "Not sure" }] },
                    { options: (catalogue.data ?? []).map((s) => ({ value: s.id, label: s.name })) },
                  ]}
                />
              )}
            </div>
            {service && service.categories.length > 0 ? (
              <div className="field">
                <label className="field-label" htmlFor="category">
                  Category <span className="field-optional">optional</span>
                </label>
                <Select
                  id="category"
                  label="Category"
                  value={categoryId}
                  onChange={(value) => {
                    setCategoryId(value);
                    setSubcategoryId("");
                  }}
                  groups={[
                    { options: [{ value: "", label: "Not sure" }] },
                    { options: service.categories.map((c) => ({ value: c.id, label: c.name })) },
                  ]}
                />
              </div>
            ) : null}
            {category && category.subcategories.length > 0 ? (
              <div className="field">
                <label className="field-label" htmlFor="subcategory">
                  Subcategory <span className="field-optional">optional</span>
                </label>
                <Select
                  id="subcategory"
                  label="Subcategory"
                  value={subcategoryId}
                  onChange={setSubcategoryId}
                  groups={[
                    { options: [{ value: "", label: "Not sure" }] },
                    { options: category.subcategories.map((c) => ({ value: c.id, label: c.name })) },
                  ]}
                />
              </div>
            ) : null}
          </div>
          {service && service.status !== "operational" ? (
            <Notice tone={service.status === "maintenance" ? "info" : "warning"}>
              <strong className="known-issue-title">
                Known issue: {service.name} is {SERVICE_STATES[service.status].label.toLowerCase()}.
              </strong>{" "}
              {service.status_message ?? SERVICE_STATES[service.status].fallback} Only raise a ticket if it's a different
              problem.
            </Notice>
          ) : null}

          <TextField
            label="Title"
            hint="e.g. Outlook closes when opening a shared calendar"
            value={title}
            maxLength={200}
            required
            error={fieldError("title")}
            onChange={(event) => setTitle(event.target.value)}
          />
          {other ? null : <SuggestedFixes text={title} serviceId={serviceId} />}
          <TextAreaField
            label="Description"
            hint={request ? "What's needed, for whom, and by when." : "Steps, error messages and when it started."}
            value={description}
            maxLength={20000}
            rows={6}
            required
            error={fieldError("description")}
            onChange={(event) => setDescription(event.target.value)}
          />

          <fieldset className="form-section">
            <legend className="form-legend">Impact</legend>
            <div className="option-grid option-grid-3">
              {IMPACTS.map((value) => {
                const Icon = IMPACT_ICONS[value];
                return (
                  <label key={value} className="option-tile option-tile-compact">
                    <input type="radio" name="impact" checked={impact === value} onChange={() => setImpact(value)} />
                    <span className="option-icon" aria-hidden="true">
                      <Icon size={20} />
                    </span>
                    <span className="option-text">
                      <span className="option-label">{IMPACT_LABELS[value]}</span>
                      <span className="option-detail">{IMPACT_DETAILS[value]}</span>
                    </span>
                  </label>
                );
              })}
            </div>
            {impact === "multiple" ? (
              isConfidential ? (
                <p className="field-hint">Confidential tickets can't be shared.</p>
              ) : (
                <div className="team-share">
                  <PeopleField
                    label="People affected"
                    addLabel="Add people"
                    hint="Optional. They can view, reply and confirm the fix."
                    people={team}
                    onChange={setTeam}
                    exclude={[me.email, ...(other && person ? [person.email] : [])]}
                  />
                </div>
              )
            ) : null}
          </fieldset>
          <fieldset className="form-section">
            <legend className="form-legend">Urgency</legend>
            <div className="option-grid option-grid-3">
              {URGENCIES.map((value) => {
                const Icon = URGENCY_ICONS[value];
                return (
                  <label key={value} className="option-tile option-tile-compact">
                    <input type="radio" name="urgency" checked={urgency === value} onChange={() => setUrgency(value)} />
                    <span className="option-icon" aria-hidden="true">
                      <Icon size={20} />
                    </span>
                    <span className="option-text">
                      <span className="option-label">{URGENCY_LABELS[value]}</span>
                      <span className="option-detail">{(request ? URGENCY_DETAILS_REQUEST : URGENCY_DETAILS)[value]}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <div className="field">
            <span className="field-label">Attachments</span>
            <span className="field-hint">Screenshots, logs or documents. Up to {MAX_FILES} files, 10 MB each.</span>
            {files.length ? (
              <ul className="file-list">
                {files.map((file, index) => (
                  <li key={`${file.name}-${index}`} className="file-chip">
                    <Paperclip size={14} aria-hidden="true" />
                    <span className="file-chip-name">{file.name}</span>
                    <span className="muted">{formatBytes(file.size)}</span>
                    <button
                      type="button"
                      className="icon-btn icon-btn-sm"
                      aria-label={`Remove ${file.name}`}
                      onClick={() => setFiles(files.filter((_, i) => i !== index))}
                    >
                      <X size={14} aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <div>
              <input
                ref={fileInput}
                id="files"
                type="file"
                multiple
                accept={ACCEPT}
                className="visually-hidden"
                onChange={(event) => addFiles(event.target.files)}
              />
              <label htmlFor="files" className="btn btn-secondary btn-sm">
                <Paperclip size={16} aria-hidden="true" />
                Attach files
              </label>
            </div>
            {fileError ? <span className="field-error">{fileError}</span> : null}
          </div>

          <div className="toggle-list">
            <ToggleRow
              label="Confidential"
              description={
                forcedConfidential
                  ? "Tickets for this service are always confidential."
                  : "For HR, security or personal matters. Only the people handling it can see it."
              }
              checked={isConfidential}
              disabled={forcedConfidential}
              onChange={setConfidential}
            />
          </div>

          {create.isError && server.length === 0 ? <Notice tone="error">{errorMessage(create.error)}</Notice> : null}
          <div className="form-actions">
            <Link to={back.to} className="btn btn-secondary">
              Cancel
            </Link>
            <Button type="submit" variant="primary" size="lg" loading={create.isPending}>
              Raise ticket
            </Button>
          </div>
        </form>
        <aside className="new-ticket-aside" aria-label="About your ticket">
          <ExpectedResponse priority={suggestedPriority(impact, urgency)} email={me.email_updates} other={other} />
          {other ? null : <ExistingTickets serviceId={serviceId} serviceName={service?.name} />}
          <Tips request={request} />
        </aside>
      </div>
    </>
  );
}

/** The ticket's priority, updated as impact and urgency change. Severity only: the time targets
 * behind due dates are for support staff (DECISIONS #58). */
function ExpectedResponse({ priority, email, other }: { priority: number; email: boolean; other: boolean }) {
  return (
    <section className="card aside-card" aria-labelledby="expected-response">
      <h2 id="expected-response" className="aside-title">
        <Gauge size={18} aria-hidden="true" />
        Priority
      </h2>
      <p className={`expected-priority expected-p${priority}`} aria-live="polite">
        <span className="expected-label">{PRIORITY_LABELS[priority]}</span>
      </p>
      {other ? (
        <p className="aside-text">
          Set from impact and urgency. They'll get updates in the desk{email ? " and by email" : ""}.
        </p>
      ) : (
        <p className="aside-text">
          Set from impact and urgency. Updates arrive here{email ? " and by email" : ""}.
        </p>
      )}
    </section>
  );
}

/** Your open tickets (for the chosen service, if any), so you don't raise the same thing twice. */
function ExistingTickets({ serviceId, serviceName }: { serviceId: string; serviceName: string | undefined }) {
  const open = useTickets({ scope: "mine", status: OPEN_STATUSES, service_id: serviceId || undefined, limit: 3 });
  const items = open.data?.items ?? [];
  if (!items.length) return null;
  return (
    <section className="card aside-card" aria-labelledby="existing-tickets">
      <h2 id="existing-tickets" className="aside-title">
        Already reported?
      </h2>
      <p className="aside-text">
        Your open tickets{serviceName ? ` for ${serviceName}` : ""}. If it's the same problem, add to that one instead.
      </p>
      <ul className="aside-list">
        {items.map((ticket) => (
          <li key={ticket.id}>
            <Link to={`/tickets/${ticket.reference}`} className="aside-ticket">
              <span className="aside-ticket-title">{ticket.title}</span>
              <span className="aside-ticket-meta">
                {ticket.reference} <StatusBadge status={ticket.status} />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

const TIPS = [
  "What you were doing",
  "The exact error, or a screenshot",
  "When it started",
  "Who else is affected",
];
const REQUEST_TIPS = [
  "Exactly what you need",
  "Who it's for",
  "When you need it",
  "Any approval you already have",
];

function Tips({ request }: { request: boolean }) {
  return (
    <section className="card aside-card" aria-labelledby="ticket-tips">
      <h2 id="ticket-tips" className="aside-title">
        <Lightbulb size={18} aria-hidden="true" />
        {request ? "Tips for a quick answer" : "Tips for a quick fix"}
      </h2>
      <ul className="tip-list">
        {(request ? REQUEST_TIPS : TIPS).map((tip) => (
          <li key={tip}>{tip}</li>
        ))}
      </ul>
    </section>
  );
}
